<script setup lang="ts">
import { ref, watch, onMounted, computed } from 'vue';
import { useScroll } from '@vueuse/core';

import AppSvgIcon from '../appSvgIcon/AppSvgIcon.vue';

import type { AppHorizontalSelectorItem } from './AppHorizontalSelector.model';

export interface AppHorizontalSelectorProps {
  items: AppHorizontalSelectorItem[];
  selected: string;
}

const props = defineProps<AppHorizontalSelectorProps>();

export interface AppHorizontalSelectorEmits {
  (eventName: 'select', selectedId: string): void;
}

const emit = defineEmits<AppHorizontalSelectorEmits>();

const containerRef = ref<HTMLElement>();

const { x: scrollX, arrivedState } = useScroll(containerRef, {
  behavior: 'smooth',
});

const canScrollLeft = computed(() => !arrivedState.left);
const canScrollRight = computed(() => !arrivedState.right);
const wasScrollLeftVisible = ref<boolean>(false);
const wasScrollRightVisible = ref<boolean>(false);

const indicatorStyle = ref<Record<string, string>>({});

const selectedIndex = computed(() =>
  props.items.findIndex((item) => item.id === props.selected)
);

function handleItemClick(item: AppHorizontalSelectorItem): void {
  if (item.disabled) {
    return;
  }
  emit('select', item.id);
}

function updateIndicatorPosition(): void {
  const container = containerRef.value;
  if (!container) {
    return;
  }

  const selectedItem = container.children[selectedIndex.value] as HTMLElement;
  if (!selectedItem) {
    return;
  }

  const { offsetLeft: left, offsetWidth: width } = selectedItem;

  indicatorStyle.value = {
    transform: `translateX(${left}px)`,
    width: `${width}px`,
  };

  const itemOverflowRight: boolean =
    left + width > scrollX.value + container.clientWidth;
  const itemOverflowLeft: boolean = left < scrollX.value;
  const isLastItem = selectedIndex.value === props.items.length - 1;
  const maxScroll = container.scrollWidth - container.clientWidth;

  if (isLastItem) {
    wasScrollRightVisible.value = true;
  } else {
    wasScrollRightVisible.value = false;
  }

  if (selectedIndex.value === 0 && scrollX.value < 1) {
    wasScrollLeftVisible.value = true;
  } else {
    wasScrollLeftVisible.value = false;
  }

  if (itemOverflowRight || itemOverflowLeft) {
    if (isLastItem) {
      scrollX.value = maxScroll;
    } else {
      scrollX.value = left;
    }
  }
}

function scroll(direction: 'left' | 'right'): void {
  const container = containerRef.value;
  if (!container || !container.children.length) {
    return;
  }

  const firstItem = container.children[0] as HTMLElement;
  if (!firstItem) {
    return;
  }
  if (direction === 'right') {
    wasScrollLeftVisible.value = false;
  }

  if (direction === 'left') {
    wasScrollRightVisible.value = false;
  }
  const scrollAmount = firstItem.offsetWidth;
  let newPosition =
    direction === 'left'
      ? scrollX.value - scrollAmount
      : scrollX.value + scrollAmount;
  if (direction === 'left' && newPosition < scrollAmount) {
    wasScrollLeftVisible.value = true;
    newPosition = 0;
  } else if (direction === 'right') {
    const maxScroll = container.scrollWidth - container.clientWidth;
    if (newPosition >= maxScroll) {
      wasScrollRightVisible.value = true;
      newPosition = maxScroll;
    }
  }
  scrollX.value = newPosition;
}

watch(
  () => props.selected,
  () => {
    updateIndicatorPosition();
  },
  { immediate: true }
);

onMounted(() => {
  updateIndicatorPosition();
});
</script>

<template>
  <div class="app-horizontal-selector">
    <button
      v-if="canScrollLeft && !wasScrollLeftVisible"
      class="scroll-button scroll-left"
      @click="scroll('left')"
    >
      <AppSvgIcon name="chevronLeft" size="24" />
    </button>

    <div ref="containerRef" class="items-container">
      <button
        v-for="item in props.items"
        :key="item.id"
        class="selector-item"
        :class="{
          selected: item.id === props.selected,
          disabled: item.disabled,
        }"
        :disabled="item.disabled"
        @click="handleItemClick(item)"
      >
        {{ item.label }}
      </button>
      <div class="selected-indicator" :style="indicatorStyle" />
    </div>

    <button
      v-if="canScrollRight && !wasScrollRightVisible"
      class="scroll-button scroll-right"
      @click="scroll('right')"
    >
      <AppSvgIcon name="chevronRight" size="24" />
    </button>
  </div>
</template>

<style scoped lang="scss">
@import '../../styles/_variables.scss';
@import '../../styles/_fonts.scss';
@import '../../styles/_media.scss';

.app-horizontal-selector {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;

  .scroll-button {
    all: unset;
    display: flex;
    align-items: center;
    justify-content: center;
    width: var(--spacing-24);
    height: var(--spacing-24);
    cursor: pointer;
    color: var(--color-neutral-70);

    &.scroll-left {
      margin-right: var(--spacing-8);
    }

    &.scroll-right {
      margin-left: var(--spacing-8);
    }
  }

  .items-container {
    position: relative;
    display: flex;
    flex: 1;
    overflow-x: auto;
    scrollbar-width: none;

    .selector-item {
      all: unset;
      @include font-label-m-light;
      padding: var(--spacing-12) var(--spacing-48);
      text-align: center;
      cursor: pointer;
      &.selected {
        @include font-label-m-bold;
      }

      &.disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .selected-indicator {
      position: absolute;
      bottom: 0;
      height: var(--spacing-4);
      background: var(--color-brand-violet);
      border-radius: var(--border-radius-4);
      transition: transform var(--transition-medium),
        width var(--transition-medium);
    }
  }

  @include for-less-or-equal-than-599-px {
    .scroll-button {
      width: var(--spacing-32);
      height: var(--spacing-32);

      &.scroll-left {
        margin-right: var(--spacing-4);
      }

      &.scroll-right {
        margin-left: var(--spacing-4);
      }
    }
  }
}
</style>
